import { useState } from 'react';
import { format } from 'date-fns';
import { clsx } from 'clsx';
import PageHeader from '@/components/common/PageHeader';
import DataTable, { type Column } from '@/components/common/DataTable';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ErrorBanner from '@/components/common/ErrorBanner';
import { useAuditLogs } from '@/hooks/useAuditLogs';
import type { AuditLogEntry } from '@/types';

const ACTION_OPTIONS = [
  { value: '', label: 'All actions' },
  { value: 'auth.login', label: 'auth.login' },
  { value: 'auth.login_failed', label: 'auth.login_failed' },
  { value: 'auth.password_changed', label: 'auth.password_changed' },
  { value: 'user.created', label: 'user.created' },
  { value: 'user.otp_regenerated', label: 'user.otp_regenerated' },
  { value: 'user.enabled', label: 'user.enabled' },
  { value: 'user.disabled', label: 'user.disabled' },
  { value: 'namespace.stopped', label: 'namespace.stopped' },
  { value: 'namespace.started', label: 'namespace.started' },
  { value: 'aws.ec2.started', label: 'aws.ec2.started' },
  { value: 'aws.ec2.stopped', label: 'aws.ec2.stopped' },
  { value: 'aws.rds.started', label: 'aws.rds.started' },
  { value: 'aws.rds.stopped', label: 'aws.rds.stopped' },
];

const RESOURCE_OPTIONS = [
  { value: '', label: 'All resources' },
  { value: 'user', label: 'user' },
  { value: 'auth', label: 'auth' },
  { value: 'namespace', label: 'namespace' },
  { value: 'ec2_instance', label: 'ec2_instance' },
  { value: 'rds_instance', label: 'rds_instance' },
];

function actionTone(action: string): string {
  if (action.startsWith('auth.login_failed')) {
    return 'bg-red-50 text-red-700 ring-red-200/80 dark:bg-red-950/40 dark:text-red-300';
  }
  if (action.startsWith('auth.')) {
    return 'bg-emerald-50 text-emerald-700 ring-emerald-200/80 dark:bg-emerald-950/40 dark:text-emerald-300';
  }
  if (action.startsWith('user.')) {
    return 'bg-brand-50 text-brand-700 ring-brand-200/80 dark:bg-brand-950/40 dark:text-brand-300';
  }
  if (action.startsWith('namespace.')) {
    return 'bg-amber-50 text-amber-800 ring-amber-200/80 dark:bg-amber-950/40 dark:text-amber-300';
  }
  if (action.startsWith('aws.')) {
    return 'bg-orange-50 text-orange-700 ring-orange-200/80 dark:bg-orange-950/40 dark:text-orange-300';
  }
  return 'bg-slate-100 text-slate-700 ring-slate-200/80 dark:bg-slate-800 dark:text-slate-300';
}

function formatDetails(details: Record<string, unknown>): string {
  const keys = Object.keys(details);
  if (keys.length === 0) return '—';
  try {
    const text = JSON.stringify(details);
    return text.length > 80 ? `${text.slice(0, 77)}…` : text;
  } catch {
    return '—';
  }
}

export default function AdminAuditLogs() {
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState('');
  const [resourceFilter, setResourceFilter] = useState('');
  const pageSize = 25;

  const { data, isLoading, error, refetch } = useAuditLogs({
    page,
    page_size: pageSize,
    action: actionFilter || undefined,
    resource_type: resourceFilter || undefined,
  });

  const columns: Column<AuditLogEntry>[] = [
    {
      key: 'created_at',
      header: 'Time (UTC)',
      sortable: true,
      sortValue: (row) => row.created_at,
      render: (row) => (
        <span className="whitespace-nowrap font-mono text-xs text-slate-600 dark:text-slate-400">
          {format(new Date(row.created_at), 'yyyy-MM-dd HH:mm:ss')}
        </span>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      render: (row) => (
        <span
          className={clsx(
            'inline-flex rounded-md px-2 py-0.5 font-mono text-xs font-medium ring-1 ring-inset',
            actionTone(row.action),
          )}
        >
          {row.action}
        </span>
      ),
    },
    {
      key: 'actor',
      header: 'Actor',
      render: (row) => (
        <div className="text-sm">
          <div className="font-medium text-slate-900 dark:text-slate-100">
            {row.actor_display_name || row.actor_email || '—'}
          </div>
          {row.actor_email && row.actor_display_name && (
            <div className="text-xs text-slate-500 dark:text-slate-400">{row.actor_email}</div>
          )}
        </div>
      ),
    },
    {
      key: 'resource_type',
      header: 'Resource',
      render: (row) => (
        <span className="text-sm text-slate-600 dark:text-slate-400">
          {row.resource_type}
          {row.resource_id ? (
            <span className="mt-0.5 block font-mono text-xs text-slate-400">{row.resource_id}</span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'ip_address',
      header: 'IP',
      render: (row) => (
        <span className="font-mono text-xs text-slate-500 dark:text-slate-400">{row.ip_address ?? '—'}</span>
      ),
    },
    {
      key: 'details',
      header: 'Details',
      render: (row) => (
        <span className="font-mono text-xs text-slate-500 dark:text-slate-400" title={JSON.stringify(row.details, null, 2)}>
          {formatDetails(row.details)}
        </span>
      ),
    },
  ];

  if (isLoading) return <LoadingSpinner message="Loading audit logs..." />;
  if (error) {
    return (
      <ErrorBanner
        title="Failed to load audit logs"
        message={error instanceof Error ? error.message : 'Request failed'}
        onRetry={refetch}
      />
    );
  }

  const items = data?.items ?? [];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Audit logs"
        description="Security and admin activity recorded by the portal. Older rows are removed automatically (default retention: 3 days)."
      />

      <div className="flex flex-wrap items-center gap-3">
        <select
          value={actionFilter}
          onChange={(e) => {
            setActionFilter(e.target.value);
            setPage(1);
          }}
          className="rounded-lg border border-slate-200/60 bg-white px-3 py-2 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
        >
          {ACTION_OPTIONS.map((opt) => (
            <option key={opt.value || 'all'} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          value={resourceFilter}
          onChange={(e) => {
            setResourceFilter(e.target.value);
            setPage(1);
          }}
          className="rounded-lg border border-slate-200/60 bg-white px-3 py-2 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
        >
          {RESOURCE_OPTIONS.map((opt) => (
            <option key={opt.value || 'all'} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <DataTable
        columns={columns}
        data={items}
        keyExtractor={(row) => row.id}
        page={page}
        pageSize={pageSize}
        total={data?.total}
        onPageChange={setPage}
        emptyTitle="No audit events"
        emptyDescription="Activity will appear here after logins, user changes, or namespace actions."
      />
    </div>
  );
}
